import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { ReactNode, useState, useEffect } from 'react';
import { Fragment } from 'react/jsx-runtime';
import ChevronDown from './ChevronDown';

type DynamicAccordion = {
  items: {
    title: string;
    subtitle?: string;
    content: ReactNode | Array<ReactNode>;
  }[]
}

const DynamicAccordion = ({ items }: DynamicAccordion) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [key, setKey] = useState(0);

  // Force re-render of Disclosure components when openIndex changes
  useEffect(() => {
    setKey(prev => prev + 1);
  }, [openIndex]);

  return (
    <div className="w-full divide-y divide-solid divide-grey-600">
      {items.map((item, index) => (
        <Disclosure 
          key={`${item.title}-${index}-${key}`}
          defaultOpen={index === openIndex}
        >
          {({ open }) => {
            // When this disclosure opens, update the openIndex and focus the first marker link
            useEffect(() => {
              if (open) {
                setOpenIndex(index);
                const firstButton: HTMLButtonElement | null = document.querySelector(`[data-markerlink]`);
                if (firstButton) {
                  firstButton.focus();
                }
              } else if (openIndex === index) {
                setOpenIndex(null);
              }
            }, [open]);

            return (
              <div className="bg-white overflow-hidden">
                <DisclosureButton
                  data-disclosurebtn
                  className="flex w-full justify-between gap-4 bg-grey-50 p-10 text-left text-sm font-medium hover:bg-grey-100"
                >
                  <div className="flex flex-col">
                    <span className="h5">
                      {item.title}
                    </span>
                    {item.subtitle && (
                      <span className="h6 mt-1">
                        ({item.subtitle})
                      </span>
                    )}
                  </div>
                  <ChevronDown open={open} />
                </DisclosureButton>
                <div className="overflow-hidden">
                  <DisclosurePanel transition className="text-sm text-gray-600 px-10">
                    <div className="flex flex-col gap-8 pt-8 pb-4">
                      {Array.isArray(item.content) ? (
                        item.content.map((contentItem, contentIndex) => (
                          <Fragment key={contentIndex}>
                            {contentItem}
                          </Fragment>
                        ))
                      ) : (
                        item.content
                      )}
                    </div>
                  </DisclosurePanel>
                </div>
              </div>
            );
          }}
        </Disclosure>
      ))}
    </div>
  );
};

export default DynamicAccordion;